import { useState } from 'react';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import Image from 'next/image';
import {
  clearPersistentCookies,
  clearPosthogCookies,
  clearSegmentCookies,
  clearGoogleAnalyticsCookies,
  clearGoogleAdsCookies,
  clearMetaCookies,
  clearTikTokCookies,
  clearMicrosoftCookies,
  getCookieConsent,
  setCookieConsent,
} from '@/lib/utils/cookie-consent';
import { getEdgeUrl } from '@/lib/getEdgeUrl';
import Toggle from '@/components/commons/ReebeloCareSection/Toggle';
import i18n from '@/settings/i18n';

const t = i18n.init('cookies');

enum AnalyticsPartnerKey {
  Posthog = 'posthog',
  Segment = 'segment',
  GoogleAnalytics = 'googleAnalytics',
}

enum AdvertisingPartnerKey {
  GoogleAds = 'googleAds',
  Meta = 'meta',
  TikTok = 'tiktok',
  Microsoft = 'microsoft',
}

const ANALYTICS_PARTNERS: Array<{
  key: AnalyticsPartnerKey;
  label: string;
  description: string;
}> = [
  {
    key: AnalyticsPartnerKey.Posthog,
    label: 'PostHog',
    description: 'Session recording and product analytics.',
  },
  {
    key: AnalyticsPartnerKey.Segment,
    label: 'Segment',
    description: 'Customer data and event tracking.',
  },
  {
    key: AnalyticsPartnerKey.GoogleAnalytics,
    label: 'Google Analytics',
    description: 'Traffic and behaviour analysis.',
  },
];

const ADVERTISING_PARTNERS: Array<{
  key: AdvertisingPartnerKey;
  label: string;
  description: string;
}> = [
  {
    key: AdvertisingPartnerKey.GoogleAds,
    label: 'Google Ads',
    description: 'Ad conversion measurement and targeting.',
  },
  {
    key: AdvertisingPartnerKey.Meta,
    label: 'Meta (Facebook)',
    description: 'Ad targeting, retargeting and conversion measurement.',
  },
  {
    key: AdvertisingPartnerKey.TikTok,
    label: 'TikTok',
    description: 'Ad targeting and conversion tracking.',
  },
  {
    key: AdvertisingPartnerKey.Microsoft,
    label: 'Microsoft Bing Ads',
    description: 'Conversion measurement and remarketing.',
  },
];

interface CookieBannerProps {
  onConsentChange: () => void;
}

function PartnerPanel<K extends string>({
  partners,
  consent,
  onToggle,
  onSetAll,
}: {
  partners: Array<{ key: K; label: string; description: string }>;
  consent: Record<K, boolean>;
  onToggle: (key: K) => void;
  onSetAll: (value: boolean) => void;
}) {
  return (
    <div className="border-t border-gray-200 bg-gray-100">
      <div className="flex items-center justify-end gap-2 px-4 py-3">
        <button
          onClick={() => onSetAll(false)}
          className="rounded-full border border-black px-4 py-1.5 text-xs font-bold text-black transition"
        >
          {t`Reject All`}
        </button>
        <button
          onClick={() => onSetAll(true)}
          className="rounded-full bg-black px-4 py-1.5 text-xs font-bold text-white transition"
        >
          {t`Accept All`}
        </button>
      </div>
      <div className="divide-y divide-gray-200 border-t border-gray-200">
        {partners.map((partner) => (
          <div
            key={partner.key}
            className="flex items-start justify-between gap-4 px-4 py-3 pl-8"
          >
            <div className="space-y-0.5">
              <p className="text-xs font-bold">{partner.label}</p>
              <p className="text-xs text-gray-700">{t(partner.description)}</p>
            </div>
            <Toggle
              isActive={consent[partner.key]}
              onClick={() => onToggle(partner.key)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CookieBanner({
  onConsentChange,
}: CookieBannerProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [advertisingOpen, setAdvertisingOpen] = useState(false);

  const [analyticsConsent, setAnalyticsConsent] = useState<
    Record<AnalyticsPartnerKey, boolean>
  >(() => {
    const stored = getCookieConsent();

    return {
      posthog: stored?.posthog ?? false,
      segment: stored?.segment ?? false,
      googleAnalytics: stored?.googleAnalytics ?? false,
    };
  });

  const [advertisingConsent, setAdvertisingConsent] = useState<
    Record<AdvertisingPartnerKey, boolean>
  >(() => {
    const stored = getCookieConsent();

    return {
      googleAds: stored?.googleAds ?? false,
      meta: stored?.meta ?? false,
      tiktok: stored?.tiktok ?? false,
      microsoft: stored?.microsoft ?? false,
    };
  });

  const anyAnalyticsEnabled = ANALYTICS_PARTNERS.some(
    (p) => analyticsConsent[p.key],
  );
  const anyAdvertisingEnabled = ADVERTISING_PARTNERS.some(
    (p) => advertisingConsent[p.key],
  );

  function setAllAnalytics(value: boolean) {
    setAnalyticsConsent({
      posthog: value,
      segment: value,
      googleAnalytics: value,
    });
  }

  function setAllAdvertising(value: boolean) {
    setAdvertisingConsent({
      googleAds: value,
      meta: value,
      tiktok: value,
      microsoft: value,
    });
  }

  function toggleAnalytics(key: AnalyticsPartnerKey) {
    setAnalyticsConsent((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function toggleAdvertising(key: AdvertisingPartnerKey) {
    setAdvertisingConsent((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function accept(): void {
    setCookieConsent({
      posthog: true,
      segment: true,
      googleAnalytics: true,
      googleAds: true,
      meta: true,
      tiktok: true,
      microsoft: true,
    });
    onConsentChange();
  }

  function reject(): void {
    clearPersistentCookies();
    setCookieConsent({
      posthog: false,
      segment: false,
      googleAnalytics: false,
      googleAds: false,
      meta: false,
      tiktok: false,
      microsoft: false,
    });
    onConsentChange();
  }

  function saveManaged(): void {
    if (!analyticsConsent.posthog) {
      clearPosthogCookies();
    }

    if (!analyticsConsent.segment) {
      clearSegmentCookies();
    }

    if (!analyticsConsent.googleAnalytics) {
      clearGoogleAnalyticsCookies();
    }

    if (!advertisingConsent.googleAds) {
      clearGoogleAdsCookies();
    }

    if (!advertisingConsent.meta) {
      clearMetaCookies();
    }

    if (!advertisingConsent.tiktok) {
      clearTikTokCookies();
    }

    if (!advertisingConsent.microsoft) {
      clearMicrosoftCookies();
    }

    setCookieConsent({
      posthog: analyticsConsent.posthog,
      segment: analyticsConsent.segment,
      googleAnalytics: analyticsConsent.googleAnalytics,
      googleAds: advertisingConsent.googleAds,
      meta: advertisingConsent.meta,
      tiktok: advertisingConsent.tiktok,
      microsoft: advertisingConsent.microsoft,
    });
    onConsentChange();
  }

  return (
    // eslint-disable-next-line tailwindcss/no-custom-classname
    <div className="fixed inset-x-4 bottom-[calc(env(safe-area-inset-bottom)+var(--cookie-banner-offset,0px)+var(--country-banner-offset,0px))] top-4 z-[60] flex max-w-[782px] items-end sm:bottom-[max(3rem,calc(env(safe-area-inset-bottom)+var(--cookie-banner-offset,0px)+var(--country-banner-offset,0px)))] sm:left-6 sm:right-6 sm:top-12 lg:bottom-[max(3rem,var(--country-banner-offset,0px))]">
      <div
        className="
          flex
          max-h-full
          w-full
          flex-col
          overflow-hidden
          rounded-[10px]
          bg-[radial-gradient(33.44%_86.37%_at_1.47%_-9.33%,#FFF3E2_0%,#FFFFFF_100%)]
          px-[30px] py-[30px]
          shadow-[0_1px_3px_rgba(0,0,0,0.12),0_12px_32px_rgba(0,0,0,0.18)]
        "
      >
        {expanded ? (
          <div className="flex min-h-0 flex-1 flex-col gap-4">
            <div className="flex shrink-0 items-center justify-between">
              <p className="text-[16px] font-bold">{t`Cookie Settings`}</p>
              <button
                onClick={() => setExpanded(false)}
                aria-label={t`Close`}
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-700 text-white transition duration-150 ease-in-out hover:scale-110 focus:outline-none"
              >
                <X size={14} />
              </button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
              <p className="text-xs sm:text-sm">
                {t`You can control how we use cookies and similar technologies. Some cookies are required for the site to function properly, while others help us improve your experience and show relevant content.`}
              </p>
              <div className="divide-y divide-gray-300 rounded-lg border border-gray-300">
                {/* Technically necessary — always on */}
                <div className="flex items-start justify-between gap-4 p-4">
                  <div className="space-y-1">
                    <p className="text-[13px] font-bold">
                      {t`Technically necessary cookies`}
                    </p>
                    <p className="text-xs">
                      {t`Essential for the site to function — shopping cart, secure checkout, fraud prevention, and site security.`}
                    </p>
                  </div>
                  <Toggle isActive={true} isEnabled={false} />
                </div>

                {/* Analytics cookies */}
                <div>
                  <div className="flex items-start justify-between gap-4 p-4">
                    <div className="flex-1 space-y-1">
                      <p className="text-[13px] font-bold">{t`Analytics cookies`}</p>
                      <p className="text-xs">
                        {t`Help us understand how visitors use the site and improve our services.`}
                      </p>
                      <button
                        onClick={() => setAnalyticsOpen((o) => !o)}
                        className="flex items-center gap-1 text-xs font-semibold underline"
                      >
                        {analyticsOpen ? (
                          <>
                            {t`Hide partners`} <ChevronUp size={13} />
                          </>
                        ) : (
                          <>
                            {t`Show partners`} <ChevronDown size={13} />
                          </>
                        )}
                      </button>
                    </div>
                    <Toggle
                      isActive={anyAnalyticsEnabled}
                      onClick={() => setAllAnalytics(!anyAnalyticsEnabled)}
                    />
                  </div>
                  {analyticsOpen && (
                    <PartnerPanel
                      partners={ANALYTICS_PARTNERS}
                      consent={analyticsConsent}
                      onToggle={toggleAnalytics}
                      onSetAll={setAllAnalytics}
                    />
                  )}
                </div>

                {/* Advertising cookies */}
                <div>
                  <div className="flex items-start justify-between gap-4 p-4">
                    <div className="flex-1 space-y-1">
                      <p className="text-[13px] font-bold">
                        {t`Advertising cookies`}
                      </p>
                      <p className="text-xs">
                        {t`Used to deliver relevant ads and measure the effectiveness of advertising campaigns.`}
                      </p>
                      <button
                        onClick={() => setAdvertisingOpen((o) => !o)}
                        className="flex items-center gap-1 text-xs font-semibold underline"
                      >
                        {advertisingOpen ? (
                          <>
                            {t`Hide partners`} <ChevronUp size={13} />
                          </>
                        ) : (
                          <>
                            {t`Show partners`} <ChevronDown size={13} />
                          </>
                        )}
                      </button>
                    </div>
                    <Toggle
                      isActive={anyAdvertisingEnabled}
                      onClick={() => setAllAdvertising(!anyAdvertisingEnabled)}
                    />
                  </div>
                  {advertisingOpen && (
                    <PartnerPanel
                      partners={ADVERTISING_PARTNERS}
                      consent={advertisingConsent}
                      onToggle={toggleAdvertising}
                      onSetAll={setAllAdvertising}
                    />
                  )}
                </div>
              </div>
            </div>

            <button
              onClick={saveManaged}
              className="
                min-h-[50px]
                w-full
                shrink-0
                rounded-md
                bg-black px-4
                py-2 text-xs font-bold
                text-white
                transition
                sm:text-sm
              "
            >
              {t`Save`}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-4 sm:items-center sm:justify-between">
            <div className="flex w-full flex-col gap-3 text-left sm:items-start">
              <div className="flex items-start gap-3">
                <Image
                  src={getEdgeUrl('images/cookie.svg')}
                  width={47}
                  height={45}
                  alt="Cookie"
                  className="h-10 w-10 shrink-0"
                />
                <p className="text-[16px] font-bold">
                  {t`We use cookies and similar technologies to make our site work, understand how it's used, and support advertising.`}
                </p>
              </div>
              <p className="text-xs sm:text-sm">
                {t`By clicking`} <span className="font-bold">{t`Accept`}</span>
                {t`, you agree to our use of optional cookies for these purposes, as described in our`}{' '}
                <a href="/policies/cookie-policy" className="underline">
                  {t`Cookie Policy`}
                </a>
                {t`. You can manage or change your preferences at any time via`}{' '}
                <a href="/policies/cookie-settings" className="underline">
                  {t`Cookie Settings`}
                </a>{' '}
                {t`at the bottom of any page.`}
              </p>
              <span
                onClick={reject}
                className="mx-auto block cursor-pointer pb-2 pt-1 text-center text-xs font-bold underline sm:text-sm"
              >
                {t`Reject All`}
              </span>
              <div className="flex w-full gap-3">
                <button
                  onClick={() => setExpanded(true)}
                  className="
                  min-h-[50px]
                  flex-1
                  rounded-md border
                  border-black px-4
                  py-2 text-xs font-bold
                  text-black
                  transition
                  sm:text-sm
                  "
                >
                  {t`Manage Cookies`}
                </button>

                <button
                  onClick={accept}
                  className="
                  min-h-[50px]
                  flex-1
                  rounded-md
                  bg-black px-4
                  py-2 text-xs font-bold
                  text-white
                  transition
                  sm:text-sm
                  "
                >
                  {t`Accept All`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
